import { Text } from '@cretesuite/design-system'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { checkmarkCircleOutline } from 'ionicons/icons'
import { useEffect, useMemo, useState } from 'react'
import {
  type Control,
  type FieldValues,
  useForm,
  useWatch,
} from 'react-hook-form'
import { useMutation, useQuery } from 'urql'
import { TermsAcknowledgment } from '../components/TermsAcknowledgment'
import { Signature } from '../components/form/Signature'
import { InventoryItemVisibility } from '../gql/graphql'
import {
  CollectOrderSignature,
  GetCustomerContacts,
  GetWorkTicketDataForSignature,
} from '../graphql/signature'
import { submitQueueable } from '../helpers/submitQueueable'
import { useNetwork } from '../hooks/useNetwork'
import { useToast } from '../hooks/useToast'

interface FormValues {
  personId?: number
  image: string
}

interface WorkTicketSignatureModalProps {
  orderId: number
  onDismiss?: (completed?: boolean) => void
}

export const WorkTicketSignatureModal = ({
  orderId,
  onDismiss,
}: WorkTicketSignatureModalProps) => {
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const [presentToast] = useToast()
  const [completed, setCompleted] = useState(false)

  const form = useForm<FormValues>()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = form

  const personId = useWatch({ control, name: 'personId' })

  // Fetch order data
  const [orderResult] = useQuery({
    query: GetWorkTicketDataForSignature,
    variables: { orderId },
  })
  const order = orderResult.data?.order
  const orderContacts = order?.contacts || []
  const customerId = order?.customer?.id
  const terms = order?.terms

  // Fetch customer contacts
  const [customerContactsResult] = useQuery({
    query: GetCustomerContacts,
    variables: { customerId: customerId || -1 },
    pause: customerId === undefined,
  })
  const customerContacts =
    customerContactsResult.data?.customers?.[0]?.contacts || []

  // Mutation
  const [, collectSignature] = useMutation(CollectOrderSignature)

  // Contacts
  const contacts = useMemo(() => {
    const orderContactsSorted = orderContacts
      .slice()
      .sort((a, b) => {
        if (a.role?.slug === 'field') return -1
        if (b.role?.slug === 'field') return 1
        if (a.default) return -1
        if (b.default) return 1
        return (a.role?.sort || 0) - (b.role?.sort || 0)
      })
      .map(({ contact }) => contact)

    const contactIds = new Set(orderContactsSorted.map((c) => c.id))
    const additionalContacts = customerContacts.filter(
      (c) => !contactIds.has(c.id),
    )
    return [...orderContactsSorted, ...additionalContacts]
  }, [orderContacts, customerContacts])

  useEffect(() => {
    if (contacts.length > 0) {
      setValue('personId', contacts[0].id)
    }
  }, [contacts, setValue])

  // Work ticket summary data
  const operatorDetails = useMemo(
    () => order?.actuals?.find((a) => a.subType === 'operator'),
    [order],
  )

  const operators = useMemo(
    () =>
      order?.assignments
        ?.slice()
        .sort((a, b) => (a.roleDetails?.sort || 0) - (b.roleDetails?.sort || 0))
        .map(({ operator }) =>
          [operator.firstName, operator.lastName].filter(Boolean).join(' '),
        )
        .filter(Boolean) || [],
    [order],
  )

  const equipment = order?.billable?.equipment || order?.planned?.equipment
  const schedule = useMemo(() => {
    const sorted = (operatorDetails?.schedule || []).slice().sort((a, b) => {
      if (a.startTime && b.startTime)
        return a.startTime.localeCompare(b.startTime)
      return (a.scheduleIndex ?? 0) - (b.scheduleIndex ?? 0)
    })

    const times: [string, string][] = []
    for (const entry of sorted) {
      if (!entry.startTime) continue
      const label =
        entry.stepDetails?.name === 'Prep'
          ? _(msg`Arrive on Site`)
          : entry.stepDetails?.name === 'Pour'
            ? _(msg`Pour Start`)
            : `${entry.stepDetails?.name ?? _(msg`Unknown`)} ${_(msg`Start`)}`
      const time = new Date(entry.startTime).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
      times.push([label, time])
    }

    const last = sorted[sorted.length - 1]
    if (last?.endTime) {
      const time = new Date(last.endTime).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
      times.push([_(msg`Depart from Site`), time])
    }

    return times
  }, [operatorDetails, _])

  const metrics = useMemo(() => {
    const result: Record<string, number | string> = {}
    for (const entry of operatorDetails?.schedule || []) {
      for (const metric of entry.metrics || []) {
        if (metric.value == null) continue
        if (typeof metric.value === 'number') {
          result[metric.key] =
            ((result[metric.key] as number) || 0) + metric.value
        } else {
          result[metric.key] = [result[metric.key], metric.value]
            .filter((v) => v != null)
            .join(', ')
        }
      }
    }
    return result
  }, [operatorDetails])

  const inventory = useMemo(
    () =>
      operatorDetails?.inventory?.filter(
        ({ item, quantity }) =>
          item.visibility?.customerWorkTicket ===
            InventoryItemVisibility.Always ||
          (item.visibility?.customerWorkTicket ===
            InventoryItemVisibility.Auto &&
            quantity),
      ) || [],
    [operatorDetails],
  )

  const handleDismiss = () => onDismiss?.(completed)

  const onFormSubmit = async (data: FormValues) => {
    // Contacts exist but none is selected — the default-select effect lost a
    // race; block to avoid persisting an unattributed signature.
    if (contacts.length > 0 && data.personId == null) {
      presentToast({
        color: 'danger',
        message: _(msg`Please select a name`),
        duration: 3000,
      })
      return
    }
    const exec = collectSignature({
      data: {
        ...data,
        metadata: {
          upsert: [{ key: 'workTicket', value: true }],
        },
      },
      where: { id: orderId },
    })

    // Never block on the network: the signature is optimistic + queued. Offline
    // or a too-slow connection → confirm and sync in the background; a
    // responsive connection still surfaces real server errors.
    const outcome = await submitQueueable(exec, { isOnline })
    if (outcome.status === 'queued') {
      presentToast({
        message: _(msg`Saved. It will sync when you're back online.`),
        duration: 3000,
      })
      setCompleted(true)
      return
    }
    if (outcome.status === 'error') {
      const errMsg = outcome.result.error?.message ?? _(msg`An error occurred`)
      presentToast({
        color: 'danger',
        message: `${_(msg`Error`)}: ${errMsg}`,
        duration: 4000,
      })
      return
    }
    setCompleted(true)
  }

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            {_(msg`Work Ticket`)} #{orderId}
          </IonTitle>
          <IonButtons slot='end'>
            <IonButton onClick={handleDismiss}>{_(msg`Close`)}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        {/* Summary card */}
        <div
          style={{
            margin: '16px',
            padding: '18px 20px',
            background: 'var(--cs-color-surface, #fff)',
            borderRadius: '5px',
            boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
          }}
        >
          {/* Top row: Billable + Operator */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
            {equipment && (
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: 'var(--cs-color-text-secondary, #888)',
                    letterSpacing: '0.4px',
                    marginBottom: '4px',
                  }}
                >
                  {_(msg`Billable`)}
                </div>
                <div
                  style={{
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'var(--cs-color-text, #1a1a1a)',
                  }}
                >
                  {equipment.displayName || equipment.name}
                </div>
              </div>
            )}
            {operators.length > 0 && (
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: 'var(--cs-color-text-secondary, #888)',
                    letterSpacing: '0.4px',
                    marginBottom: '4px',
                  }}
                >
                  {_(msg`Operator`)}
                  {operators.length !== 1 && 's'}
                </div>
                {operators.map((op) => (
                  <div
                    key={op}
                    style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: 'var(--cs-color-text, #1a1a1a)',
                    }}
                  >
                    {op}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metrics row */}
          {Object.keys(metrics).length > 0 && (
            <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
              {Object.entries(metrics).map(([key, value]) => (
                <div key={key} style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      color: 'var(--cs-color-text-secondary, #888)',
                      letterSpacing: '0.4px',
                      marginBottom: '4px',
                    }}
                  >
                    {key === 'cubicYards'
                      ? _(msg`Cubic Yards`)
                      : key.replace(/([A-Z])/g, ' $1').trim()}
                  </div>
                  <div
                    style={{
                      fontSize: '15px',
                      fontWeight: 600,
                      color: 'var(--cs-color-text, #1a1a1a)',
                    }}
                  >
                    {value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Inventory */}
          {inventory.length > 0 && (
            <div style={{ marginBottom: '14px' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--cs-color-text-secondary, #888)',
                  letterSpacing: '0.4px',
                  marginBottom: '6px',
                }}
              >
                {_(msg`System`)}
              </div>
              {inventory.map((orderItem) => (
                <div
                  key={orderItem.item.id}
                  style={{
                    fontSize: '14px',
                    color: 'var(--cs-color-text, #1a1a1a)',
                    padding: '2px 0',
                  }}
                >
                  {orderItem.quantity != null && (
                    <span style={{ fontWeight: 600, marginRight: '6px' }}>
                      {orderItem.quantity}
                      {orderItem.item.displayUnit || 'x'}
                    </span>
                  )}
                  {orderItem.item.name}
                </div>
              ))}
            </div>
          )}

          {/* Schedule */}
          {schedule.length > 0 && (
            <div>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--cs-color-text-secondary, #888)',
                  letterSpacing: '0.4px',
                  marginBottom: '6px',
                }}
              >
                {_(msg`Schedule`)}
              </div>
              {schedule.map(([label, time]) => (
                <div
                  key={label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '4px 0',
                    fontSize: '14px',
                    color: 'var(--cs-color-text, #333)',
                  }}
                >
                  <span>{label}</span>
                  <span style={{ fontWeight: 600 }}>{time}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signature form */}
        {!completed && (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            style={{ padding: '0 16px' }}
          >
            {/* Name selector */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px 0',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <span
                style={{
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'var(--cs-color-text-secondary, #555)',
                  minWidth: '70px',
                }}
              >
                {_(msg`Name`)}
              </span>
              <IonSelect
                interface='action-sheet'
                value={personId}
                onIonChange={(e) => setValue('personId', e.detail.value)}
                style={{ fontSize: '15px', '--padding-start': '0' }}
              >
                {contacts.map((contact) => (
                  <IonSelectOption key={contact.id} value={contact.id}>
                    {contact.firstName} {contact.lastName}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </div>

            {/* Signature pad */}
            <div style={{ marginTop: '12px' }}>
              {errors.image && (
                <Text
                  variant='body'
                  className='text-danger'
                  style={{
                    fontSize: '0.8em',
                    fontWeight: 700,
                    marginBottom: '4px',
                  }}
                >
                  {errors.image.message}
                </Text>
              )}
              <Signature
                name='image'
                control={control as unknown as Control<FieldValues>}
                rules={{ required: _(msg`Please sign in the box below`) }}
                style={{ minHeight: '200px', width: '100%' }}
              />
            </div>

            {/* Terms + submit */}
            <div style={{ textAlign: 'center', padding: '14px 0 24px' }}>
              <TermsAcknowledgment
                content={terms?.content}
                textStyle={{
                  fontSize: '12px',
                  lineHeight: '16px',
                  color: 'var(--cs-color-text-secondary, #888)',
                }}
              />
              <button
                type='submit'
                style={{
                  display: 'block',
                  minWidth: '70%',
                  margin: '14px auto 0',
                  padding: '16px',
                  fontSize: '16px',
                  fontWeight: 700,
                  fontFamily: 'inherit',
                  color: '#fff',
                  backgroundColor: 'var(--ion-color-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                }}
              >
                {_(msg`Submit`)}
              </button>
            </div>
          </form>
        )}

        {/* Success view */}
        {completed && (
          <button
            type='button'
            className='ion-padding ion-text-center'
            style={{
              border: 'none',
              background: 'none',
              padding: 0,
              margin: 0,
              font: 'inherit',
              color: 'inherit',
              cursor: 'pointer',
              textAlign: 'center' as const,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
            }}
            onClick={handleDismiss}
          >
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background:
                  'linear-gradient(135deg, var(--cs-color-success-bg, #e8f5e9), var(--cs-color-success-bg, #c8e6c9))',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '20px',
                boxShadow: '0 4px 16px rgba(76,175,80,0.12)',
              }}
            >
              <IonIcon
                icon={checkmarkCircleOutline}
                style={{
                  fontSize: '40px',
                  color: 'var(--cs-color-success, #4caf50)',
                }}
              />
            </div>
            <div
              style={{
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--cs-color-text, #1a1a1a)',
                marginBottom: '8px',
              }}
            >
              {_(msg`Signature Accepted`)}
            </div>
            <div
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--cs-color-text-secondary, #999)',
                lineHeight: '1.4',
              }}
            >
              {_(msg`Please return this device to the Operator.`)}
            </div>
          </button>
        )}
      </IonContent>
    </>
  )
}

export default WorkTicketSignatureModal
